import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { TenantService } from 'app/services/tenant.service';
import * as tenantActions from 'app/actions/tenant';
import * as fromTenant from 'app/reducers/tenant';
import { TenantReducerUtils } from 'app/utils/reducers/tenant-reducer.utils';
import { TenantDto, TenantUpdateDto } from 'app/models/api/tenant';
import { TenantAccount } from 'app/models/reducers/tenant';
import { concatLatestFrom } from '@ngrx/operators';

@Injectable()
export class TenantEffects {
  private actions$: Actions = inject(Actions);
  private store: Store = inject(Store);
  private tenantService: TenantService = inject(TenantService);

  pageEnter$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType(tenantActions.pageEnter),
    map(() => tenantActions.loadTenantInitial()),
  ));

  // API effects
  loadTenantInitial$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType(tenantActions.loadTenantInitial),
    switchMap(() => this.tenantService.getTenant().pipe(
      map((response: TenantDto) => tenantActions.loadTenantInitialSuccess({ response })),
      catchError(error => of(tenantActions.loadTenantInitialFailed({ error }))),
    )),
  ));

  saveTenant$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType(tenantActions.saveTenant),
    concatLatestFrom(() => [
      this.store.select(fromTenant.selectTenantId),
      this.store.select(fromTenant.selectName),
      this.store.select(fromTenant.selectAccounts),
    ]),
    map(([, tenantId, name, accounts]: [Action, string, string, TenantAccount[]]) =>
      TenantReducerUtils.createTenantUpdateDto(tenantId, name, accounts)),
    switchMap((body: TenantUpdateDto) => this.tenantService.saveTenant(body).pipe(
      map((response: TenantDto) => tenantActions.saveTenantSuccess({ response })),
      catchError(error => of(tenantActions.saveTenantFail({ error }))),
    )),
  ));
}
